import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const fmtDelta = (n) => (n > 0 ? `+${money(n)}` : n < 0 ? `-${money(Math.abs(n))}` : money(0));
const deltaClass = (n) => (n > 0 ? "text-rose-600" : n < 0 ? "text-emerald-600" : "text-slate-400");
const STATUS_TONE = {
  added: "bg-emerald-50 text-emerald-700 border-emerald-200",
  removed: "bg-rose-50 text-rose-700 border-rose-200",
  changed: "bg-amber-50 text-amber-700 border-amber-200",
  unchanged: "bg-slate-50 text-slate-500 border-slate-200",
};

// Side-by-side dollar comparison of two bid revisions (or a revision vs the live "Current").
export function RevisionCompareDialog({ open, onOpenChange, bidId, revisions }) {
  const options = [
    ...(revisions || []).map((r) => ({ id: r.revision_id, label: `Revision ${r.revision}` })),
    { id: "current", label: "Current (working)" },
  ];
  const [a, setA] = useState("");
  const [b, setB] = useState("current");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && (revisions || []).length && !a) setA(revisions[0].revision_id);
  }, [open, revisions, a]);

  useEffect(() => {
    if (!open || !a || !b) return;
    setLoading(true);
    api.get(`/bids/${bidId}/revision-compare-data`, { params: { a, b } })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [open, a, b, bidId]);

  const exportXlsx = async () => {
    try {
      const params = {};
      if (a !== "current") params.revision_id = a;
      if (b !== "current") params.to_revision_id = b;
      const res = await api.get(`/bids/${bidId}/revision-compare`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data); const el = document.createElement("a");
      el.href = url; el.download = "revision-compare.xlsx"; el.click(); URL.revokeObjectURL(url);
    } catch { const { toast } = await import("sonner"); toast.error("Excel export needs two saved revisions."); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="revision-compare-dialog">
        <DialogHeader>
          <DialogTitle>Compare revisions</DialogTitle>
          <DialogDescription>See exactly what changed between two versions — line by line, in dollars.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 text-sm">
          <select data-testid="rev-compare-a" value={a} onChange={(e) => setA(e.target.value)}
            className="border border-slate-200 rounded-md h-9 px-2 flex-1 bg-white">
            {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
          <select data-testid="rev-compare-b" value={b} onChange={(e) => setB(e.target.value)}
            className="border border-slate-200 rounded-md h-9 px-2 flex-1 bg-white">
            {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>
        {loading && <div className="text-xs text-slate-400 py-6 text-center" data-testid="rev-compare-loading">Comparing…</div>}
        {data && !loading && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Stat label={data.a_label} value={money(data.a_total)} />
              <Stat label={data.b_label} value={money(data.b_total)} />
              <div className="rounded-lg border border-slate-200 p-3 text-center" data-testid="rev-compare-total-delta">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Difference</div>
                <div className={`text-lg font-mono font-semibold ${deltaClass(data.total_delta)}`}>{fmtDelta(data.total_delta)}</div>
                {data.total_delta_pct != null && (
                  <div className={`text-[11px] ${deltaClass(data.total_delta)}`}>{data.total_delta_pct > 0 ? "+" : ""}{data.total_delta_pct}%</div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {data.categories.filter((c) => c.a || c.b).map((c) => (
                <div key={c.key} className="rounded-lg border border-slate-100 px-2.5 py-2" data-testid={`rev-compare-cat-${c.key}`}>
                  <div className="text-[10px] text-slate-400">{c.label}</div>
                  <div className={`text-xs font-mono ${deltaClass(c.delta)}`}>{fmtDelta(c.delta)}</div>
                </div>
              ))}
            </div>
            <div className="max-h-72 overflow-auto border border-slate-100 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Line</th>
                    <th className="text-right px-3 py-2 font-medium">{data.a_label}</th>
                    <th className="text-right px-3 py-2 font-medium">{data.b_label}</th>
                    <th className="text-right px-3 py-2 font-medium">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.length === 0 && (
                    <tr><td colSpan={4} className="px-3 py-4 text-center text-slate-400">No scope lines to compare.</td></tr>
                  )}
                  {data.rows.map((r, i) => (
                    <tr key={i} className="border-t border-slate-50" data-testid={`rev-compare-row-${i}`}>
                      <td className="px-3 py-1.5">
                        <span className={`inline-block mr-2 text-[9px] px-1.5 py-0.5 rounded border ${STATUS_TONE[r.status]}`}>{r.status}</span>
                        {r.name}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-slate-500">{money(r.a_subtotal)}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-slate-700">{money(r.b_subtotal)}</td>
                      <td className={`px-3 py-1.5 text-right font-mono ${deltaClass(r.delta)}`}>{fmtDelta(r.delta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={exportXlsx} data-testid="rev-compare-export">Download Excel</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3 text-center">
      <div className="text-[10px] uppercase tracking-wide text-slate-400 truncate">{label}</div>
      <div className="text-lg font-mono font-semibold text-slate-800">{value}</div>
    </div>
  );
}
